// /inbox - the unmatched-document queue: files that arrived without a
// confident case match. Assign each to its matter or save it to the firm
// resource library. The admin's screen: the server refuses everyone else,
// so the page says so itself and never asks. No fixture path: the queue is
// structurally empty until a Shared Drive inbox feeds it, and the page
// says so.

import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AdminOnlyCard } from "../../components/chrome/AdminOnlyCard";
import { Skeleton, SkeletonGroup } from "../../components/chrome/Skeleton";
import { SepList } from "../../components/chrome/SepList";
import { caseTypeLabel } from "../../lib/caseLabels";
import { useUnmatched } from "../../hooks/useUnmatched";
import { assignUnmatched, saveAsResource, searchAssignTargets } from "../../api/unmatched";
import type { UnmatchedDoc } from "../../api/unmatched";
import { SEARCH_MIN_QUERY, SEARCH_DEBOUNCE_MS } from "../../hooks/useGlobalSearch";
import { useCurrentUser } from "../../components/auth/CurrentUserContext";
import { isAdminRole } from "../../lib/roles";

// Same rhythm as the command palette over the same unindexed search.

function rowSub(d: UnmatchedDoc): string[] {
  const parts: string[] = [];
  if (d.aiSummary) parts.push(d.aiSummary);
  if (d.source) parts.push(`via ${d.source}`);
  if (d.createdAt) parts.push(`arrived ${d.createdAt.slice(0, 10)}`);
  return parts;
}

export function InboxIndex() {
  const user = useCurrentUser();
  const isAdmin = isAdminRole(user?.role);
  const { data, isLoading, isError } = useUnmatched(isAdmin);
  const queryClient = useQueryClient();
  // Optimistic removal: a row leaves the list the moment its action fires
  // and comes back with an error line if the server refuses.
  const [hiddenIds, setHiddenIds] = useState<string[]>([]);
  const [rowError, setRowError] = useState<{ id: string; message: string } | null>(null);
  const [pickerFor, setPickerFor] = useState<string | null>(null);
  const [pickerQ, setPickerQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(pickerQ.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [pickerQ]);

  const hide = (id: string) => setHiddenIds((ids) => [...ids, id]);
  const unhide = (id: string) => setHiddenIds((ids) => ids.filter((x) => x !== id));

  const settle = async () => {
    await queryClient.invalidateQueries({ queryKey: ["unmatched"] });
    // The rail badge reads the same number from the firm aggregate.
    await queryClient.invalidateQueries({ queryKey: ["firm", "counts"] });
  };

  const assign = useMutation({
    mutationFn: ({ docId, caseId }: { docId: string; caseId: string }) =>
      assignUnmatched(docId, caseId),
    onMutate: ({ docId }) => {
      setRowError(null);
      setPickerFor(null);
      hide(docId);
    },
    onError: (err, { docId }) => {
      unhide(docId);
      setRowError({
        id: docId,
        message: err instanceof Error ? err.message : "Assign failed",
      });
    },
    onSuccess: async (_r, { docId }) => {
      await settle();
      unhide(docId);
    },
  });

  const resource = useMutation({
    mutationFn: (docId: string) => saveAsResource(docId),
    onMutate: (docId) => {
      setRowError(null);
      hide(docId);
    },
    onError: (err, docId) => {
      unhide(docId);
      setRowError({
        id: docId,
        message: err instanceof Error ? err.message : "Save as resource failed",
      });
    },
    onSuccess: async (_r, docId) => {
      await settle();
      unhide(docId);
    },
  });

  const targets = useQuery({
    queryKey: ["assign-targets", debouncedQ],
    queryFn: () => searchAssignTargets(debouncedQ),
    enabled: pickerFor !== null && debouncedQ.length >= SEARCH_MIN_QUERY,
    staleTime: 30_000,
  });

  const docs = (data?.documents ?? []).filter((d) => !hiddenIds.includes(d._id));
  const capped = data ? data.total > data.documents.length : false;

  if (!isAdmin) return <AdminOnlyCard user={user} what="read the inbox" />;

  if (isLoading) {
    return (
      <main className="cc-main">
        <div className="cc-body">
          <SkeletonGroup label="Loading inbox">
            <Skeleton width="30%" height={22} />
            <Skeleton width="100%" height={84} radius={10} />
            <Skeleton width="100%" height={84} radius={10} />
          </SkeletonGroup>
        </div>
      </main>
    );
  }

  return (
    <main className="cc-main">
      <div className="cc-body">
        <div className="cc-index-head">
          <span className="title">Inbox</span>
          {data && (
            <span className="count">
              {data.total} {data.total === 1 ? "document" : "documents"} awaiting review
            </span>
          )}
          <span className="blurb">
            Documents that arrived without a confident case match. Assign each to its matter or save
            it to the firm resource library.
            {capped ? ` Showing the newest ${data?.documents.length} of ${data?.total}.` : ""}
          </span>
        </div>

        {isError && (
          <div
            className="cc-card"
            style={{ padding: "10px 18px", marginBottom: 12, color: "var(--warn-600)" }}
          >
            Could not reach the server - the inbox queue is unavailable.
          </div>
        )}

        {data && docs.length === 0 && (
          <div className="cc-card" style={{ padding: "10px 18px", color: "var(--muted)" }}>
            Nothing waiting to be matched. Documents land here when the Drive inbox cannot
            confidently match them to a case.
          </div>
        )}

        {docs.length > 0 && (
          <div className="cc-card">
            {docs.map((d) => {
              const sc = d.suggestedCase;
              const sub = rowSub(d);
              return (
                <div
                  key={d._id}
                  className="cc-index-row"
                  style={{ display: "block", cursor: "default" }}
                >
                  <div className="row-title">{d.fileName}</div>
                  {sub.length > 0 && (
                    <div className="row-sub">
                      <SepList parts={sub} />
                    </div>
                  )}

                  {sc && (
                    <div className="row-sub" style={{ marginTop: 4 }}>
                      Suggested match: {sc.clientName}
                      {(sc.eId ?? sc.caseNumber) ? (
                        <>
                          {" "}
                          <span className="tnum-mono">{sc.eId ?? sc.caseNumber}</span>
                        </>
                      ) : null}
                    </div>
                  )}

                  <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
                    {sc && (
                      <button
                        type="button"
                        className="cc-link-btn"
                        onClick={() => assign.mutate({ docId: d._id, caseId: sc._id })}
                      >
                        Assign to suggested
                      </button>
                    )}
                    <button
                      type="button"
                      className="cc-link-btn"
                      onClick={() => {
                        setPickerFor(pickerFor === d._id ? null : d._id);
                        setPickerQ("");
                        // Immediately, not after the debounce: switching rows
                        // must never leave the previous row's candidates
                        // clickable under an empty box.
                        setDebouncedQ("");
                      }}
                    >
                      {sc ? "Assign elsewhere" : "Assign to a case"}
                    </button>
                    <button
                      type="button"
                      className="cc-link-btn"
                      onClick={() => resource.mutate(d._id)}
                    >
                      Save as firm resource
                    </button>
                  </div>

                  {pickerFor === d._id && (
                    <div style={{ marginTop: 8 }}>
                      <input
                        autoFocus
                        aria-label="Search cases to assign"
                        placeholder="Client name, eID, A-number..."
                        value={pickerQ}
                        onChange={(e) => setPickerQ(e.target.value)}
                        className="cc-field"
                        style={{ width: "100%", maxWidth: 420 }}
                      />
                      {targets.isFetching && (
                        <div className="row-sub" style={{ marginTop: 4 }}>
                          Searching...
                        </div>
                      )}
                      {targets.isError && (
                        <div className="row-sub" style={{ marginTop: 4, color: "var(--warn-600)" }}>
                          Search failed - try again.
                        </div>
                      )}
                      {targets.data &&
                        targets.data.length === 0 &&
                        !targets.isFetching &&
                        debouncedQ.length >= SEARCH_MIN_QUERY && (
                          <div className="row-sub" style={{ marginTop: 4 }}>
                            No open matters match.
                          </div>
                        )}
                      {(targets.data ?? []).map((t) => (
                        <button
                          key={t._id}
                          type="button"
                          className="cc-link-btn"
                          style={{ display: "block", marginTop: 4 }}
                          onClick={() => assign.mutate({ docId: d._id, caseId: t._id })}
                        >
                          {t.clientName ?? "Unnamed case"}
                          {t.eId ? (
                            <>
                              {" "}
                              <span className="tnum-mono">{t.eId}</span>
                            </>
                          ) : (
                            ""
                          )}
                          {t.caseType ? ` (${caseTypeLabel(t.caseType)})` : ""}
                        </button>
                      ))}
                    </div>
                  )}

                  {rowError?.id === d._id && (
                    <div className="row-sub" style={{ marginTop: 6, color: "var(--red-700)" }}>
                      {rowError.message}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </main>
  );
}
